/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react/jsx-one-expression-per-line */
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { format, parseISO } from 'date-fns';
import React from 'react';
import CurrencyFormat from 'react-currency-format';
import { Modal } from 'rsuite';
import { ModalProps } from 'rsuite/lib/Modal';
import { Exception } from '../../data';
import { PriceEvent, PriceEventHistory } from '../../pages/IPA/RevisaoPrecos/services_ts';
import { AlertWithTimestamp } from '../AlertWithTimestamp';
import { ButtonPrimary } from '../ButtonPrimary';
import { Link } from '../Link';
import styles from './ModalPriceEventHistory.module.scss';

type EventItemProps = React.HTMLAttributes<HTMLLIElement> & {
  noBullet?: boolean;
};

const EventItem = ({ noBullet, className, ...props }: EventItemProps) => (
  <li className={classNames(styles['event-item'], noBullet && styles['no-bullet'], className)} {...props} />
);

type ArchitectureLegendProps = Pick<PriceEvent, 'architectureId' | 'architectureRefProduct'>;

const ArchitectureLegend = ({ architectureId, architectureRefProduct }: ArchitectureLegendProps) => {
  if (!architectureId || !architectureRefProduct) return null;

  return (
    <span className={styles['architecture-legend']}>
      reflexo da arquitetura de preços {architectureId}, produto ref. {architectureRefProduct}
    </span>
  );
};

const getEventComponent = (ocurrence: PriceEvent) => {
  switch (ocurrence.eventType) {
    case 'ERP':
      return (
        <EventItem>
          <span className={styles.title}>
            Preço vigente recebido{' '}
            <strong>
              <CurrencyFormat
                decimalScale={2}
                prefix="R$ "
                decimalSeparator=","
                value={ocurrence.price || undefined}
                displayType="text"
                thousandSeparator="."
                fixedDecimalScale
              />
            </strong>
          </span>
          <span className={styles.subtitle}>por ERP</span>
        </EventItem>
      );
    case 'RULE':
      return (
        <EventItem>
          <span className={styles.title}>
            Novo preço sugerido{' '}
            <strong>
              <CurrencyFormat
                decimalScale={2}
                prefix="R$ "
                decimalSeparator=","
                value={ocurrence.price || undefined}
                fixedDecimalScale
                displayType="text"
                thousandSeparator="."
              />
            </strong>
          </span>
          <span className={styles.subtitle}>
            por Regra{' '}
            <Link className={styles.link} to={`/ipa/regras-preco/${ocurrence.author.id}`}>
              {ocurrence.author.name}
            </Link>
            {ocurrence.trigger && (
              <>
                ,<br />
                como produto derivado de {ocurrence.trigger}
              </>
            )}
          </span>
          <ArchitectureLegend architectureId={ocurrence.architectureId} architectureRefProduct={ocurrence.architectureRefProduct} />
        </EventItem>
      );
    case 'LIMIT':
      return (
        <EventItem>
          <span className={styles.title}>
            {ocurrence.brokenPrice || ocurrence.adjustedPriceValue != null ? 'Preço com quebra de limite' : 'Preço não possui quebra de limite'}
            {ocurrence.adjustedPriceValue != null && (
              <>
                , ajustado para{' '}
                <strong>
                  <CurrencyFormat
                    decimalScale={2}
                    prefix="R$ "
                    decimalSeparator=","
                    value={ocurrence.adjustedPriceValue}
                    displayType="text"
                    thousandSeparator="."
                    fixedDecimalScale
                  />
                </strong>
              </>
            )}
          </span>
          <span className={styles.subtitle}>
            por Limite{' '}
            <Link className={styles.link} to={`/ipa/limites-preco/${ocurrence.author.id}`}>
              {ocurrence.author.name}
            </Link>
          </span>
        </EventItem>
      );
    case 'MIN_VARIATION':
      return (
        <EventItem>
          <span className={styles.title}>
            {ocurrence.brokenPrice
              ? 'Preço sugerido excede a variação mínima para alteração de preço'
              : 'Preço sugerido ficou dentro variação mínima para alteração de preço'}
          </span>
        </EventItem>
      );
    case 'RANGE':
      return (
        <EventItem>
          <span className={styles.title}>
            Aplicação de preço psicológico para{' '}
            <strong>
              <CurrencyFormat
                decimalScale={2}
                prefix="R$ "
                decimalSeparator=","
                value={ocurrence.price || undefined}
                fixedDecimalScale
                displayType="text"
                thousandSeparator="."
              />
            </strong>
          </span>
          <span className={styles.subtitle}>por Preço psicológico</span>
        </EventItem>
      );
    case 'PRICE_LIST':
      return (
        <EventItem>
          <span className={styles.title}>{ocurrence.brokenPrice ? 'Preço fora dos limites da tabela' : 'Preço dentro dos limites da tabela de preços'}</span>
          <span className={styles.subtitle}>por Tabela de Preços</span>
        </EventItem>
      );
    case 'PRICE':
      return (
        <EventItem noBullet>
          <span className={styles.title}>
            <strong>
              {ocurrence.hour} - Preço disponível do gerenciador de preços{' '}
              <strong>
                <CurrencyFormat
                  decimalScale={2}
                  prefix="R$ "
                  decimalSeparator=","
                  fixedDecimalScale
                  displayType="text"
                  thousandSeparator="."
                  value={ocurrence.price || undefined}
                />
              </strong>
            </strong>
          </span>
        </EventItem>
      );
    case 'EDITED_PRICE':
      return (
        <EventItem>
          <span className={styles.title}>
            {ocurrence.hour} Edição do preço para{' '}
            <strong>
              <CurrencyFormat
                decimalScale={2}
                prefix="R$ "
                decimalSeparator=","
                value={ocurrence.price || undefined}
                displayType="text"
                thousandSeparator="."
                fixedDecimalScale
              />
            </strong>
          </span>
          <span className={styles.subtitle}>
            por {ocurrence.author.name}
            {ocurrence.trigger && (
              <>
                ,<br />
                pela edição do preço do produto pai {ocurrence.trigger}
              </>
            )}
          </span>
          <ArchitectureLegend architectureId={ocurrence.architectureId} architectureRefProduct={ocurrence.architectureRefProduct} />
        </EventItem>
      );
    case 'APPLIED_PRICE':
      return (
        <EventItem>
          <span className={styles.title}>
            {ocurrence.hour} Aplicação do preço{' '}
            <strong>
              <CurrencyFormat
                decimalScale={2}
                prefix="R$ "
                decimalSeparator=","
                value={ocurrence.price || undefined}
                displayType="text"
                thousandSeparator="."
                fixedDecimalScale
              />
            </strong>
          </span>
          <span className={styles.subtitle}>por {ocurrence.author.name}</span>
        </EventItem>
      );
    case 'EDITED_PRICE_OFFER':
      return (
        <EventItem>
          <span className={styles.title}>
            {ocurrence.hour} - Edição do preço 03 - Oferta para{' '}
            <strong>
              <CurrencyFormat
                decimalScale={2}
                prefix="R$ "
                decimalSeparator=","
                value={ocurrence.price || undefined}
                displayType="text"
                thousandSeparator="."
                fixedDecimalScale
              />
            </strong>
          </span>
          <span className={styles.subtitle}>por {ocurrence.author.name}</span>
        </EventItem>
      );
    case 'EDITED_PRICE_WHOLESALE':
      return (
        <EventItem>
          <span className={styles.title}>
            {ocurrence.hour} - Edição do preço 02 - Atacado para{' '}
            <strong>
              <CurrencyFormat
                decimalScale={2}
                prefix="R$ "
                decimalSeparator=","
                value={ocurrence.price || undefined}
                displayType="text"
                thousandSeparator="."
                fixedDecimalScale
              />
            </strong>
          </span>
          <span className={styles.subtitle}>por {ocurrence.author.name}</span>
        </EventItem>
      );
    default:
      return null;
  }
};

const EventList = ({ className, ...props }: React.HTMLAttributes<HTMLUListElement>) => {
  return <ul {...props} className={classNames(styles['event-list'], className)} />;
};

type ModalPriceEventHistoryProps = ModalProps & {
  queryFn?: () => Promise<PriceEventHistory>;
  productsToBePricedId: string;
};

export const ModalPriceEventHistory = ({ className, show = false, queryFn, onHide, productsToBePricedId, ...props }: ModalPriceEventHistoryProps) => {
  if (!queryFn) return null;

  const { data } = useQuery({
    queryKey: ['priceEventHistory', productsToBePricedId],
    queryFn: async () => {
      try {
        const res = await queryFn();
        return res;
      } catch {
        const { message } = new Exception('Erro ao buscar histórico de preços');
        AlertWithTimestamp({ message });
      }
    },
    enabled: !!show,
    initialData: [],
  });

  return (
    <Modal className={classNames('info-modal', styles['modal-price-event-history'], className)} show={show} onHide={onHide} {...props}>
      <Modal.Header>
        <Modal.Title>Resumo de alteração de preço</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles['modal-price-event-history__content']}>
          {data?.map(({ date, occurrences }) => (
            <div key={date}>
              <span className={styles.date}>{format(parseISO(date), 'dd/MM/yyyy')}</span>
              <EventList>
                {occurrences.map((occurrence, occurrenceIndex) => {
                  return <React.Fragment key={`${occurrence.eventType}-${occurrenceIndex}`}>{getEventComponent(occurrence)}</React.Fragment>;
                })}
              </EventList>
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <ButtonPrimary theme="ghost" onClick={onHide}>
          fechar
        </ButtonPrimary>
      </Modal.Footer>
    </Modal>
  );
};
